import { useCallback, useEffect, useState } from 'react';

import classes from './style.module.scss';

import { cn } from '@/lib/utils';

import { SectionsNavProps } from '@/models/Applications';

import { ApplicationsIcon } from '../icons/ApplicationsIcon';
import UnstyledLink from '../links/UnstyledLink';
import NextImage from '../NextImage';

type UniqueSections<T extends { targetsectionid?: string }[]> =
  T[number]['targetsectionid'];
type SectionTypeUnion<T extends { targetsectionid?: string }[]> =
  | 'All'
  | UniqueSections<T>;

export default function ApplicationsDesktop(props: SectionsNavProps) {
  const { sectionnavigation } = props;
  const [hoverSection, setHoverSection] =
    useState<SectionTypeUnion<typeof sectionnavigation>>('All');
  const [hoverObject, setHoverObject] = useState(
    sectionnavigation[sectionnavigation.length - 1],
  );
  const [animate, setAnimate] = useState<boolean>(false);
  const hoverTrigger = useCallback(
    (section: SectionTypeUnion<typeof sectionnavigation>) => {
      const findsectionnavigation = sectionnavigation.find(
        (item) => item.sectionname === section,
      );

      setHoverSection(section);
      findsectionnavigation &&
        setTimeout(() => setHoverObject(findsectionnavigation), 100);
    },
    [sectionnavigation],
  );
  const handleMouseLeave = useCallback(() => {
    setHoverSection('All');
    setTimeout(
      () => setHoverObject(sectionnavigation[sectionnavigation.length - 1]),
      100,
    );
  }, [sectionnavigation]);

  useEffect(() => {
    const delay = 250;

    setAnimate(false);
    const timer = setTimeout(() => {
      setAnimate(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [hoverSection]);

  return (
    <>
      <div
        className='relative z-10 flex w-full items-center justify-center overflow-hidden rounded-2xl bg-primary-800 px-8 md:py-28'
        onMouseLeave={handleMouseLeave}
        data-aos='fade-in'
        data-aos-anchor='#applications'
      >
        <ul
          className={cn(
            'pointer-events-none absolute left-1/2 top-1/2 z-10 h-[37.5rem] w-[37.5rem] -translate-x-1/2 -translate-y-1/2 transform text-center',
            classes.applicationsTitles,
          )}
          data-aos
          data-aos-anchor='#applications'
        >
          {sectionnavigation.map((item) => {
            if (!item.sectionname) return null;

            return (
              <li key={item.targetsectionid}>
                <UnstyledLink
                  href={`#${item.targetsectionid}`}
                  className={cn(
                    'text-2xl font-normal transition-opacity duration-300 ease-linear',
                    hoverSection === 'All' || item.sectionname === hoverSection
                      ? 'opacity-100'
                      : 'opacity-30',
                  )}
                  onMouseOver={() => hoverTrigger(item.sectionname)}
                  onFocus={() => hoverTrigger(item.sectionname)}
                  scrollToAnchor
                >
                  {item.sectionname}
                </UnstyledLink>
              </li>
            );
          })}
        </ul>
        <div
          className='applications__content absolute left-1/2 top-1/2 z-10 flex h-80 w-80 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center text-center opacity-0'
          data-aos
          data-aos-anchor='#applications'
          data-aos-delay='500'
        >
          {hoverObject && (
            <div
              className={cn(
                'flex flex-col items-center justify-center',
                hoverObject.sectionname ? 'gap-11' : 'gap-5',
              )}
            >
              <div
                className={cn(
                  'relative flex items-center justify-center',
                  'opacity-0',
                  animate &&
                    'opacity-100 transition-opacity duration-300 ease-linear',
                  hoverObject.sectionname ? 'h-20 w-20' : 'h-10 w-10',
                )}
              >
                <NextImage
                  src={`/images/${hoverObject.targetsectionid}.svg`}
                  alt='Hover on section'
                  width='70'
                  height='70'
                />
              </div>
              <p
                className={cn(
                  'leading-normal opacity-0',
                  animate &&
                    'opacity-100 transition-opacity delay-75 duration-300 ease-linear',
                )}
                dangerouslySetInnerHTML={{
                  __html: hoverObject.sectiondescription,
                }}
              />
              {hoverObject.sectionname && (
                <div
                  className={cn(
                    'flex flex-col items-center gap-1 pt-8 text-sm',
                    'opacity-0',
                    animate &&
                      'opacity-100 transition-opacity delay-150 duration-300 ease-linear',
                  )}
                >
                  <UnstyledLink
                    href={`#${hoverObject.targetsectionid}`}
                    className='font-bold hover:underline'
                    scrollToAnchor
                  >
                    Click to learn more
                  </UnstyledLink>
                  <p className='font-light'>or keep scrolling</p>
                </div>
              )}
            </div>
          )}
        </div>
        <ApplicationsIcon />
      </div>
      <div
        className={cn(
          'pointer-events-none absolute left-1/2 top-1/2 -z-[10] h-full max-h-[80%] w-full max-w-[80%] -translate-x-1/2 -translate-y-1/2 transform blur-[250px]',
          hoverObject && classes[hoverObject.backgroundcolor],
          classes.background,
        )}
      ></div>
    </>
  );
}
